import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/InvoicesService';
import { Invoice } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

export const useInvoice = (invoiceId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const invoiceQuery = useQuery({
        queryKey: ['invoice', invoiceId],
        queryFn: () => fetch(invoiceId!),
        enabled: !!invoiceId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Invoice) => {
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return add(rest);
        },
        onSuccess: () => {
            notifySuccess('Invoice created');
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: Invoice) => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            const { createdByUsername: _createdByUsername, created: _created, updated: _updated, ...rest } = values;
            return update(invoiceId, rest);
        },
        onSuccess: () => {
            notifySuccess('Invoice updated');
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
            queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!invoiceId) throw new Error('invoiceId is required parameter');
            return deleteRecord(invoiceId);
        },
        onSuccess: () => {
            notifySuccess('Invoice deleted');
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
        },
        onError: notifyError
    });

    return {
        record: invoiceQuery.data,
        loading: invoiceQuery.isLoading,
        fetchRecord: invoiceQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};